import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from "@mui/material";
import { makeStyles } from "@material-ui/styles";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  WifiTethering as TestIcon,
} from "@material-ui/icons";
import {
  PrimaryButton,
  SecondaryButton,
  TextInput,
  ModalError,
  ModalSuccess,
} from "components";
import { getErrors } from "utils";
import Swal from "sweetalert2";
import {
  listDspSFTPConfigs,
  getDspSFTPConfig,
  createDspSFTPConfig,
  updateDspSFTPConfig,
  deleteDspSFTPConfig,
  testDspSFTPConnection,
} from "utils/DSPSFTPService";

const useStyles = makeStyles(() => ({
  dialogActionsSpaceBetween: {
    padding: "16px 24px",
    justifyContent: "space-between",
  },
  dialogActionsEnd: {
    padding: "16px 24px",
  },
  tableHeadCell: {
    fontWeight: "bold",
  },
  slugText: {
    fontFamily: "monospace",
  },
}));

const AUTH_TYPES = [
  { value: "password", label: "Password" },
  { value: "key", label: "SSH Key" },
  { value: "key_bastion", label: "SSH Key + Bastion" },
];

const EMPTY_FORM = {
  name: "",
  slug: "",
  host: "",
  port: "22",
  username: "",
  password: "",
  private_key: "",
  bastion_host: "",
  bastion_port: "22",
  bastion_username: "",
  bastion_private_key: "",
  remote_path: ".",
  auth_type: "password",
  is_active: true,
};

// ─── Form dialog (create / edit) ──────────────────────────────────────────────
function ConfigFormDialog({ open, editId, onClose, onSaved }) {
  const classes = useStyles();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const isEdit = Boolean(editId);

  // Load existing config when editing
  useEffect(() => {
    if (open && isEdit) {
      getDspSFTPConfig(editId)
        .then(res => {
          const d = res?.data?.data || {};
          setForm({
            name: d.name || "",
            slug: d.slug || "",
            host: d.host || "",
            port: String(d.port || "22"),
            username: d.username || "",
            password: d.password || "",
            private_key: d.private_key || "",
            bastion_host: d.bastion_host || "",
            bastion_port: String(d.bastion_port || "22"),
            bastion_username: d.bastion_username || "",
            bastion_private_key: d.bastion_private_key || "",
            remote_path: d.remote_path || ".",
            auth_type: d.auth_type || "password",
            is_active: d.is_active ?? true,
          });
        })
        .catch(err => ModalError(getErrors(err?.response)));
    } else if (open && !isEdit) {
      setForm(EMPTY_FORM);
    }
  }, [open, editId, isEdit]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...form,
        port: Number(form.port),
        bastion_port: Number(form.bastion_port),
      };
      if (isEdit) {
        await updateDspSFTPConfig(editId, payload);
      } else {
        await createDspSFTPConfig(payload);
      }
      ModalSuccess(`DSP config ${isEdit ? "updated" : "created"} successfully.`);
      onSaved();
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const payload = {
        ...form,
        port: Number(form.port) || 22,
        bastion_port: Number(form.bastion_port) || 22,
      };
      const res = await testDspSFTPConnection(payload);
      ModalSuccess(res?.data?.message || "Connection successful!");
    } catch (error) {
      ModalError(error?.response?.data?.message || getErrors(error?.response || error));
    } finally {
      setIsTesting(false);
    }
  };

  const isKey = form.auth_type === "key" || form.auth_type === "key_bastion";
  const isBastion = form.auth_type === "key_bastion";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            {isEdit ? "Edit" : "Add"} DSP SFTP Configuration
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Basic info */}
          <Grid item xs={12} sm={6}>
            <TextInput
              label="DSP Name"
              value={form.name}
              onChange={e => setField("name", e.target.value)}
              placeholder="e.g. Spotify"
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Slug"
              value={form.slug}
              onChange={e => setField("slug", e.target.value)}
              placeholder="e.g. spotify"
              width="100%"
            />
          </Grid>

          {/* Connection */}
          <Grid item xs={12} sm={8}>
            <TextInput
              label="Host"
              value={form.host}
              onChange={e => setField("host", e.target.value)}
              placeholder="sftp.example.com"
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextInput
              label="Port"
              value={form.port}
              onChange={e => setField("port", e.target.value)}
              placeholder="22"
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextInput
              label="Username"
              value={form.username}
              onChange={e => setField("username", e.target.value)}
              placeholder="sftp_user"
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextInput
              label="Remote Path"
              value={form.remote_path}
              onChange={e => setField("remote_path", e.target.value)}
              placeholder="."
              width="100%"
            />
          </Grid>

          {/* Auth type */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" mb={1}>
              Authentication Type
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              {AUTH_TYPES.map(a => (
                <Chip
                  key={a.value}
                  label={a.label}
                  clickable
                  color={form.auth_type === a.value ? "primary" : "default"}
                  onClick={() => setField("auth_type", a.value)}
                />
              ))}
            </Box>
          </Grid>

          {/* Password auth */}
          {form.auth_type === "password" && (
            <Grid item xs={12} sm={6}>
              <TextInput
                label="Password"
                type="password"
                value={form.password}
                onChange={e => setField("password", e.target.value)}
                width="100%"
              />
            </Grid>
          )}

          {/* Key auth */}
          {isKey && (
            <Grid item xs={12}>
              <TextInput
                label="Private Key (PEM)"
                value={form.private_key}
                onChange={e => setField("private_key", e.target.value)}
                placeholder="-----BEGIN RSA PRIVATE KEY-----"
                multiline
                rows={4}
                width="100%"
              />
            </Grid>
          )}

          {/* Bastion */}
          {isBastion && (
            <>
              <Grid item xs={12}>
                <Divider textAlign="left">
                  <Typography variant="caption" color="textSecondary">
                    Bastion Host
                  </Typography>
                </Divider>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextInput
                  label="Bastion Host"
                  value={form.bastion_host}
                  onChange={e => setField("bastion_host", e.target.value)}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextInput
                  label="Bastion Port"
                  value={form.bastion_port}
                  onChange={e => setField("bastion_port", e.target.value)}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  label="Bastion Username"
                  value={form.bastion_username}
                  onChange={e => setField("bastion_username", e.target.value)}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  label="Bastion Private Key (PEM)"
                  value={form.bastion_private_key}
                  onChange={e => setField("bastion_private_key", e.target.value)}
                  multiline
                  rows={4}
                  width="100%"
                />
              </Grid>
            </>
          )}

          {/* Active toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={e => setField("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActionsSpaceBetween}>
        <Box>
          {isEdit && (
            <SecondaryButton
              label={isTesting ? "Testing…" : "Test Connection"}
              onClick={handleTestConnection}
              disabled={isTesting}
              startIcon={isTesting ? <CircularProgress size={14} /> : <TestIcon />}
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          <SecondaryButton label="Cancel" onClick={onClose} />
          <PrimaryButton
            label={isSaving ? "Saving…" : "Save"}
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={14} color="inherit" /> : null}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main list dialog ──────────────────────────────────────────────────────────
function DSPSFTPConfigDialog({ open, onClose, onSaved }) {
  const classes = useStyles();
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const fetchConfigs = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listDspSFTPConfigs();
      setConfigs(res?.data?.data || []);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchConfigs();
  }, [open, fetchConfigs]);

  const handleDelete = async id => {
    const result = await Swal.fire({
      title: "Delete this DSP SFTP config?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await deleteDspSFTPConfig(id);
      ModalSuccess("Config deleted.");
      fetchConfigs();
      onSaved();
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const handleEdit = id => {
    setEditId(id);
    setFormOpen(true);
  };

  const handleAdd = () => {
    setEditId(null);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchConfigs();
    onSaved();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight="bold">
              DSP SFTP Configurations
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <PrimaryButton
                label="Add DSP"
                onClick={handleAdd}
                startIcon={<AddIcon />}
                size="small"
              />
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : configs.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="textSecondary">
                No DSP SFTP configurations found. Add one to get started.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Name</TableCell>
                  <TableCell className={classes.tableHeadCell}>Slug</TableCell>
                  <TableCell className={classes.tableHeadCell}>Host</TableCell>
                  <TableCell className={classes.tableHeadCell}>Auth</TableCell>
                  <TableCell className={classes.tableHeadCell}>Status</TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map(cfg => (
                  <TableRow key={cfg.id} hover>
                    <TableCell>{cfg.name}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        className={classes.slugText}
                      >
                        {cfg.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {cfg.host}:{cfg.port}
                    </TableCell>
                    <TableCell>
                      <Chip label={cfg.auth_type || "password"} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cfg.is_active ? "Active" : "Inactive"}
                        size="small"
                        color={cfg.is_active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(cfg.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(cfg.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogActionsEnd}>
          <SecondaryButton label="Close" onClick={onClose} />
        </DialogActions>
      </Dialog>

      {/* Form dialog */}
      <ConfigFormDialog
        open={formOpen}
        editId={editId}
        onClose={() => setFormOpen(false)}
        onSaved={handleFormSaved}
      />
    </>
  );
}

export default DSPSFTPConfigDialog;
